import { Link } from "react-router-dom";
import { ArrowLeft, Hammer, AlertTriangle, HardHat } from "lucide-react";

export default function IndustrialWorkshopsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[var(--secondary)] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Centre
          </Link>
          <h1 className="text-4xl font-bold">Industrial & Workshops</h1>
          <p className="text-gray-200 mt-2">
            Guide to finding and hosting industrial workspace and workshop facilities
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        {/* Overview */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <div className="flex items-start gap-4 mb-6">
            <Hammer className="w-8 h-8 text-[var(--secondary)] flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-3xl font-bold mb-4">
                Industrial Workspace Overview
              </h2>
              <p className="text-gray-700 leading-relaxed">
                AirArrange connects manufacturers, artisans, and makers with
                industrial spaces and workshops. Whether you need a woodworking
                shop, metalworking facility, production floor, or maker space,
                find the perfect industrial workspace for your business.
              </p>
            </div>
          </div>
        </section>

        {/* For Tenants */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <h2 className="text-3xl font-bold mb-6">For Workshop Tenants</h2>

          <div className="space-y-8">
            {/* Types of Industrial Spaces */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Types of Industrial Spaces
              </h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Light Industrial & Workshops
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Suitable for carpentry, metalworking, crafts, and small-scale
                    manufacturing.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Woodworking and carpentry shops</li>
                    <li>Metalworking and welding facilities</li>
                    <li>Artist studios and maker spaces</li>
                    <li>Small assembly operations</li>
                  </ul>
                </div>

                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Production & Manufacturing
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Larger spaces for product manufacturing and assembly lines.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Product assembly facilities</li>
                    <li>Food production and commercial kitchens</li>
                    <li>Packaging and fulfillment centers</li>
                    <li>Light manufacturing plants</li>
                  </ul>
                </div>

                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Specialized Industrial
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Facilities with specialized equipment or requirements.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Auto repair and vehicle maintenance shops</li>
                    <li>Print and design studios</li>
                    <li>Photography and video production studios</li>
                    <li>Research and development labs</li>
                  </ul>
                </div>

                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-lg mb-2">
                    Shared Maker Spaces
                  </h4>
                  <p className="text-gray-600 mb-2">
                    Community workshops with shared tools and equipment.
                  </p>
                  <ul className="text-sm text-gray-500 list-disc list-inside ml-4">
                    <li>Co-working maker spaces</li>
                    <li>Shared equipment facilities</li>
                    <li>Incubator workshops</li>
                    <li>Community craft studios</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Key Considerations */}
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <HardHat className="w-6 h-6 text-blue-600" />
                Key Considerations When Choosing
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Power & Utilities</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>
                      <strong>Electrical Capacity:</strong> Verify voltage
                      (120V, 240V, 480V) and amperage for your equipment
                    </li>
                    <li>
                      <strong>Three-Phase Power:</strong> Essential for large
                      machinery and industrial equipment
                    </li>
                    <li>
                      <strong>Compressed Air:</strong> Check availability and PSI
                      rating if needed
                    </li>
                    <li>
                      <strong>Water & Drainage:</strong> Important for some
                      manufacturing processes
                    </li>
                    <li>
                      <strong>Gas Lines:</strong> Natural gas or propane
                      availability for heating/equipment
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Space Specifications</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>
                      <strong>Ceiling Height:</strong> Minimum clearance for
                      equipment, lifts, and overhead cranes
                    </li>
                    <li>
                      <strong>Floor Load Capacity:</strong> Weight limits per
                      square foot for heavy equipment
                    </li>
                    <li>
                      <strong>Bay Doors:</strong> Size and type of loading doors
                      for material delivery
                    </li>
                    <li>
                      <strong>Floor Type:</strong> Concrete, epoxy-coated, or
                      specialized flooring
                    </li>
                    <li>
                      <strong>Climate Control:</strong> Heating and cooling
                      requirements for your processes
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Access & Loading</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>Loading dock height and accessibility</li>
                    <li>Drive-in access for delivery vehicles</li>
                    <li>Forklift and pallet jack accessibility</li>
                    <li>Parking for staff and delivery trucks</li>
                    <li>24/7 access availability</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Safety & Compliance */}
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                Safety & Compliance Requirements
              </h3>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Critical:</strong> Industrial operations have strict
                  safety and regulatory requirements. Ensure full compliance to
                  protect your workers and avoid fines.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">
                    Permits & Licenses Required
                  </h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>Business license for industrial operations</li>
                    <li>Building permits for equipment installation</li>
                    <li>Fire department permits for hazardous materials</li>
                    <li>
                      Environmental permits for emissions or waste disposal
                    </li>
                    <li>Occupational health and safety certifications</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Safety Equipment</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>Fire extinguishers and suppression systems</li>
                    <li>First aid kits and emergency eyewash stations</li>
                    <li>Proper ventilation and dust collection systems</li>
                    <li>Emergency exits and lighting</li>
                    <li>Hazardous material storage cabinets</li>
                    <li>Personal protective equipment (PPE) for all workers</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Insurance Requirements</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>
                      <strong>General Liability:</strong> Coverage for bodily
                      injury and property damage
                    </li>
                    <li>
                      <strong>Workers' Compensation:</strong> Required if you
                      have employees
                    </li>
                    <li>
                      <strong>Equipment Insurance:</strong> Protection for
                      valuable machinery and tools
                    </li>
                    <li>
                      <strong>Product Liability:</strong> If manufacturing
                      consumer goods
                    </li>
                    <li>
                      <strong>Environmental Liability:</strong> For operations
                      with pollution risks
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Workshop Setup Checklist */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Workshop Setup Checklist
              </h3>
              <div className="bg-blue-50 rounded-lg p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="zoning-check"
                    />
                    <label htmlFor="zoning-check" className="text-gray-700">
                      Verify zoning allows your type of industrial activity
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="electrical-audit"
                    />
                    <label
                      htmlFor="electrical-audit"
                      className="text-gray-700"
                    >
                      Conduct electrical audit to confirm capacity for equipment
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="ventilation"
                    />
                    <label htmlFor="ventilation" className="text-gray-700">
                      Install proper ventilation and air filtration systems
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="fire-safety" />
                    <label htmlFor="fire-safety" className="text-gray-700">
                      Set up fire safety equipment and emergency procedures
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="waste-management"
                    />
                    <label
                      htmlFor="waste-management"
                      className="text-gray-700"
                    >
                      Arrange waste management and disposal services
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" id="security" />
                    <label htmlFor="security" className="text-gray-700">
                      Install security systems for equipment protection
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="tool-storage"
                    />
                    <label htmlFor="tool-storage" className="text-gray-700">
                      Set up tool and material storage systems
                    </label>
                  </li>
                  <li className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1"
                      id="safety-training"
                    />
                    <label htmlFor="safety-training" className="text-gray-700">
                      Complete safety training for all workers
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* For Hosts */}
        <section className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold mb-6">
            For Industrial Space Hosts
          </h2>

          <div className="space-y-8">
            {/* Preparing Industrial Space */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Preparing Your Industrial Space
              </h3>
              <p className="text-gray-700 mb-3">
                Make your industrial space ready for tenants:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  Obtain professional inspection of electrical, plumbing, and
                  structural systems
                </li>
                <li>
                  Document all specifications: electrical capacity, ceiling
                  heights, floor loads
                </li>
                <li>
                  Ensure compliance with industrial zoning and building codes
                </li>
                <li>Install adequate lighting for workshop operations</li>
                <li>
                  Provide clear access routes for material delivery and removal
                </li>
                <li>
                  Repair any damage to floors, walls, or loading areas
                </li>
                <li>Clean and remove any debris or hazardous materials</li>
                <li>
                  Install basic safety equipment (fire extinguishers, emergency
                  lighting)
                </li>
              </ul>
            </div>

            {/* What to Include in Listing */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Critical Information for Your Listing
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Power & Utilities</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Electrical voltage and amperage</li>
                    <li>• Three-phase power availability</li>
                    <li>• Compressed air PSI rating</li>
                    <li>• Water and gas connections</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Physical Specs</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Total square footage</li>
                    <li>• Ceiling height (clear height)</li>
                    <li>• Floor load capacity (PSF)</li>
                    <li>• Bay door dimensions</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Access & Loading</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Loading dock specifications</li>
                    <li>• Drive-in door dimensions</li>
                    <li>• Parking spaces available</li>
                    <li>• 24/7 access options</li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Climate & Systems</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Heating and cooling systems</li>
                    <li>• Ventilation capabilities</li>
                    <li>• Insulation quality</li>
                    <li>• Environmental controls</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Tenant Screening */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Screening Industrial Tenants
              </h3>
              <p className="text-gray-700 mb-3">
                Important questions to ask prospective tenants:
              </p>
              <div className="space-y-2">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <strong>What type of work will be performed?</strong> Ensure
                    it's compatible with your space and zoning.
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <strong>What equipment will be installed?</strong> Verify
                    your space can support the power and weight requirements.
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <strong>What are the operating hours?</strong> Confirm
                    compatibility with your access policies and neighboring
                    tenants.
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <strong>What insurance coverage do they have?</strong> Verify
                    adequate liability and property insurance.
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    <strong>
                      Will hazardous materials be used or stored?
                    </strong>{" "}
                    Ensure proper permits and safety measures.
                  </p>
                </div>
              </div>
            </div>

            {/* Legal Protections */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Protecting Yourself as a Host
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  Require proof of adequate insurance before tenant move-in
                </li>
                <li>
                  Include detailed equipment and modification restrictions in
                  lease
                </li>
                <li>
                  Specify tenant responsibilities for maintenance and repairs
                </li>
                <li>
                  Conduct regular safety inspections and document conditions
                </li>
                <li>
                  Include indemnification clauses for tenant operations
                </li>
                <li>
                  Require tenants to obtain necessary permits and licenses
                </li>
                <li>
                  Consider requiring a security deposit equivalent to 2-3 months
                  rent
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
